import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, formatDate, formatDuration } from "@/lib/api";
import { getUnit } from "@/lib/weight";
import { fromCanonicalLb, toCanonicalLb, formatStoredWeight } from "@/components/app/WeightInput";
import { formatStoredDistance, formatStoredHeight } from "@/lib/units";
import WeightInput from "@/components/app/WeightInput";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, ChartLineUp, Barbell, Lightning, Timer, ArrowClockwise, Heartbeat, Plus, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import BackButton from "@/components/app/BackButton";

function Row({ label, value, sub }) {
  if (value === null || value === undefined || value === "" || value === 0) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-line last:border-0">
      <div className="hs-label text-[10px]">{label}</div>
      <div className="text-right">
        <div className="font-data font-semibold text-ink">{value}</div>
        {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
      </div>
    </div>
  );
}

function PRCard({ pr, onPick, unit }) {
  const fmt = (d) => formatDate(d, { month: "short", day: "numeric", year: "numeric" });
  // Sort 1RM, 2RM, 3RM … numerically. Top out at 10RM rows in the strip —
  // higher rep numbers are rare and visually overflow the card.
  const repKeys = Object.keys(pr.rep_maxes || {})
    .map((k) => parseInt(k, 10))
    .filter((n) => !isNaN(n) && n > 0 && n <= 10)
    .sort((a, b) => a - b);
  return (
    <div className="hs-card p-5 hover:-translate-y-1 transition-all duration-200" data-testid={`pr-card-${pr.movement}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Barbell size={18} className="text-moss shrink-0" />
          <h3 className="font-heading text-lg font-semibold truncate">{pr.movement}</h3>
        </div>
        <button
          onClick={() => onPick(pr.movement)}
          className="text-xs text-terracotta hover:text-terracotta-hover inline-flex items-center gap-1"
          data-testid={`chart-${pr.movement}`}
        >
          <ChartLineUp size={14} /> chart
        </button>
      </div>
      {repKeys.length > 0 && (
        <div className="mt-3" data-testid={`rep-maxes-${pr.movement}`}>
          <div className="hs-label text-[10px] mb-1.5">N-rep maxes</div>
          <div className="flex flex-wrap gap-1.5">
            {repKeys.map((n) => {
              const rec = pr.rep_maxes[String(n)];
              return (
                <div key={n} className="px-2.5 py-1.5 rounded-lg bg-sage/40" title={fmt(rec.at)}>
                  <div className="hs-label text-[9px] text-moss leading-none">{n}RM</div>
                  <div className="font-data text-sm font-semibold leading-tight mt-0.5">
                    {formatStoredWeight(rec.weight, unit)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {repKeys.length > 0 && (() => {
        // Epley projection: w * (1 + reps/30). Surface a nudge whenever the
        // best multi-rep set projects past the recorded 1RM (or when no 1RM
        // exists yet) so the athlete sees a concrete next target.
        let bestProj = null;
        for (const n of repKeys) {
          if (n < 2) continue;
          const w = pr.rep_maxes[String(n)].weight;
          const proj = w * (1 + n / 30);
          if (!bestProj || proj > bestProj.weight) bestProj = { weight: proj, fromReps: n, fromWeight: w };
        }
        const oneRm = pr.rep_maxes["1"]?.weight ?? 0;
        if (!bestProj || bestProj.weight <= oneRm + 0.01) return null;
        const target = Math.round(bestProj.weight);
        return (
          <div className="mt-2 px-3 py-2 rounded-lg bg-terracotta/8 border border-terracotta/25 text-[11px] text-ink-2" data-testid={`projected-1rm-${pr.movement}`}>
            <span className="font-semibold text-terracotta">Epley says</span> your {bestProj.fromWeight} {unit} × {bestProj.fromReps} projects to a ≈ {target} {unit} 1RM — worth a test soon.
          </div>
        );
      })()}
      <div className="mt-3 divide-y divide-line">
        <Row label="Heaviest" value={pr.max_weight != null ? formatStoredWeight(pr.max_weight, unit) : null} sub={pr.max_weight_reps ? `× ${pr.max_weight_reps} · ${fmt(pr.max_weight_at)}` : fmt(pr.max_weight_at)} />
        <Row label="Max reps" value={pr.max_reps != null ? pr.max_reps : null} sub={pr.max_reps_weight ? `@ ${formatStoredWeight(pr.max_reps_weight, unit)} · ${fmt(pr.max_reps_at)}` : fmt(pr.max_reps_at)} />
        <Row label="Est. 1RM" value={pr.best_1rm != null ? formatStoredWeight(pr.best_1rm, unit) : null} sub={fmt(pr.best_1rm_at)} />
        <Row label="Longest time" value={pr.longest_time != null ? `${pr.longest_time}s` : null} sub={fmt(pr.longest_time_at)} />
        <Row label="Longest distance" value={pr.longest_distance != null ? formatStoredDistance(pr.longest_distance, pr.longest_distance_unit || "m") : null} sub={fmt(pr.longest_distance_at)} />
        <Row label="Max height" value={pr.max_height != null ? formatStoredHeight(pr.max_height, pr.max_height_unit || "in") : null} sub={fmt(pr.max_height_at)} />
        <Row label="Max calories / set" value={pr.max_calories != null ? `${pr.max_calories} cal` : null} sub={fmt(pr.max_calories_at)} />
        <Row label="Total sets" value={pr.total_sets || null} sub={pr.total_volume ? `Volume ${Math.round(fromCanonicalLb(pr.total_volume, unit) || pr.total_volume).toLocaleString()} ${unit}${pr.total_calories ? ` · ${Math.round(pr.total_calories)} cal` : ""}` : (pr.total_calories ? `${Math.round(pr.total_calories)} cal` : null)} />
      </div>
    </div>
  );
}

const BLOCK_META = {
  metcon: { label: "MetCon", tint: "bg-terracotta text-white", icon: Timer },
  amrap: { label: "AMRAP", tint: "bg-warning text-white", icon: ArrowClockwise },
  emom: { label: "EMOM", tint: "bg-chart-3 text-white", icon: Lightning },
  tabata: { label: "Tabata", tint: "bg-error text-white", icon: Lightning },
  ladder: { label: "Ladder", tint: "bg-chart-2 text-white", icon: Lightning },
  circuit: { label: "Circuit", tint: "bg-ink-2 text-white", icon: Lightning },
  intervals: { label: "Intervals", tint: "bg-teal-700 text-white", icon: Timer },
};

function fmtTime(sec) {
  return formatDuration(sec) || null;
}
function fmtDate(d) {
  return formatDate(d, { month: "short", day: "numeric", year: "numeric" });
}

// Famous CrossFit benchmark WODs — we tag matches with a star + special accent.
const BENCHMARK_NAMES = new Set([
  "fran", "cindy", "murph", "helen", "grace", "diane", "elizabeth", "isabel",
  "jackie", "karen", "kelly", "annie", "barbara", "chelsea", "fight gone bad",
  "filthy fifty", "mary", "nancy", "linda", "angie", "eva", "amanda", "nicole",
  "dt", "jt", "fran", "tabata this", "tabata mash up",
]);

function ConditioningPRCard({ pr }) {
  const meta = BLOCK_META[pr.block_type] || BLOCK_META.metcon;
  const Icon = meta.icon;
  const isAmrap = pr.block_type === "amrap" || pr.block_type === "emom";
  const ivlMax = pr.block_type === "intervals" && !pr.best_time_seconds
    ? (pr.best_calories ? { str: `${pr.best_calories} cal`, label: "Most calories", at: pr.best_calories_at }
      : pr.best_distance_meters ? { str: formatStoredDistance(pr.best_distance_meters, pr.best_distance_unit || "m"), label: "Most distance", at: pr.best_distance_at }
      : null)
    : null;
  const bestStr = ivlMax ? ivlMax.str : isAmrap
    ? (pr.best_rounds != null ? `${pr.best_rounds}${pr.best_rounds_extra ? `+${pr.best_rounds_extra}` : ""}` : null)
    : fmtTime(pr.best_time_seconds);
  const bestLabel = ivlMax ? ivlMax.label : isAmrap ? "Best score" : "Best time";
  const isBenchmark = BENCHMARK_NAMES.has((pr.title || "").trim().toLowerCase());

  // Build chart data — always chronological, oldest first
  const chartData = useMemo(() => {
    const rows = [...(pr.attempts || [])]
      .filter((a) => isAmrap
        ? (a.rounds_completed != null)
        : (a.total_time_seconds != null && a.total_time_seconds > 0))
      .sort((a, b) => (a.logged_at || "").localeCompare(b.logged_at || ""))
      .map((a, i) => ({
        ...a,
        idx: i + 1,
        date: (a.logged_at || "").slice(0, 10),
        score: isAmrap
          ? (a.rounds_completed || 0) + (a.extra_reps || 0) / 1000
          : a.total_time_seconds,
      }));
    return rows;
  }, [pr.attempts, isAmrap]);

  // Find the index of the PR (lowest time / highest score)
  const prIdx = useMemo(() => {
    if (chartData.length === 0) return -1;
    if (isAmrap) {
      let best = -1, bestScore = -Infinity;
      chartData.forEach((d, i) => { if (d.score > bestScore) { bestScore = d.score; best = i; } });
      return best;
    } else {
      let best = -1, bestT = Infinity;
      chartData.forEach((d, i) => { if (d.score < bestT) { bestT = d.score; best = i; } });
      return best;
    }
  }, [chartData, isAmrap]);

  // y-axis: invert for time-based (lower = better), so the line goes UP with progress.
  // Recharts uses YAxis with `reversed`, but we'll just keep raw values and let the
  // visual reading be "down = faster" — most CF charts work that way and a small caption explains it.
  const yLabel = isAmrap ? "Score" : "Time (sec)";

  return (
    <div className={`hs-card p-5 hover:-translate-y-1 transition-all duration-200 ${isBenchmark ? "ring-1 ring-terracotta/30" : ""}`} data-testid={`cond-pr-card-${pr.title}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${meta.tint}`}>
            <Icon size={14} weight="bold" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-heading text-lg font-semibold truncate">{pr.title}</h3>
              {isBenchmark && (
                <span className="px-1.5 py-0.5 rounded-md bg-terracotta/15 text-terracotta text-[9px] font-bold uppercase tracking-[0.15em]" title="CrossFit benchmark WOD">
                  Benchmark
                </span>
              )}
            </div>
            <div className="hs-label text-[10px]">{meta.label}</div>
          </div>
        </div>
      </div>
      {bestStr ? (
        <div className="mt-3 p-3 rounded-xl bg-sage/40 border border-sage">
          <div className="hs-label text-[10px] text-moss">{bestLabel}</div>
          <div className="font-data font-semibold text-2xl text-moss leading-tight mt-0.5">{bestStr}</div>
          <div className="text-[11px] text-ink-3 mt-0.5">{fmtDate(ivlMax?.at || pr.best_time_at || pr.best_rounds_at)}</div>
        </div>
      ) : (
        <div className="mt-3 text-sm text-ink-3">No completed attempts yet.</div>
      )}

      {chartData.length >= 2 && (
        <div className="mt-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="hs-label text-[10px]">Trend · {chartData.length} attempts</div>
            <div className="text-[10px] text-ink-3">
              {isAmrap ? "higher = better" : "lower = faster"}
            </div>
          </div>
          <div className="h-24 -mx-2" data-testid={`cond-pr-spark-${pr.title}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 6, right: 8, left: 8, bottom: 6 }}>
                <CartesianGrid stroke="rgba(0,0,0,0.04)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="idx" hide />
                <YAxis dataKey="score" hide reversed={!isAmrap} domain={["auto", "auto"]} />
                <Tooltip
                  cursor={{ stroke: "#C0633F", strokeDasharray: "3 3" }}
                  contentStyle={{ background: "#F2EFE7", border: "1px solid #E5DFD0", borderRadius: 12, fontSize: 12 }}
                  formatter={(v) => [
                    isAmrap
                      ? `${Math.floor(v)}+${Math.round((v - Math.floor(v)) * 1000)}`
                      : fmtTime(v),
                    isAmrap ? "Score" : "Time",
                  ]}
                  labelFormatter={(_, payload) => {
                    if (!payload || !payload[0]) return "";
                    const p = payload[0].payload;
                    return fmtDate(p.date);
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#2D4A3E"
                  strokeWidth={2}
                  dot={(props) => {
                    const isPR = props.index === prIdx;
                    return (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={isPR ? 5 : 3}
                        fill={isPR ? "#C0633F" : "#2D4A3E"}
                        stroke={isPR ? "#fff" : "none"}
                        strokeWidth={isPR ? 2 : 0}
                      />
                    );
                  }}
                  activeDot={{ r: 6, fill: "#C0633F" }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {pr.attempts && pr.attempts.length > 1 && (
        <div className="mt-3">
          <div className="hs-label text-[10px] mb-1.5">Recent attempts</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {pr.attempts.slice(0, 6).map((a, i) => {
              const score = isAmrap
                ? (a.rounds_completed != null ? `${a.rounds_completed}${a.extra_reps ? `+${a.extra_reps}` : ""}` : "—")
                : (fmtTime(a.total_time_seconds) || "—");
              const isCurrentBest =
                isAmrap
                  ? a.rounds_completed === pr.best_rounds && a.extra_reps === pr.best_rounds_extra
                  : a.total_time_seconds === pr.best_time_seconds;
              return (
                <div key={i} className="flex items-baseline justify-between text-xs">
                  <span className="text-ink-3">{fmtDate(a.logged_at)}</span>
                  <span className={`font-data ${isCurrentBest ? "font-bold text-terracotta" : "font-semibold"}`}>
                    {score}{isCurrentBest && " ★"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function BodyPanel({ athleteId, canEdit, unit }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), weight: "", weight_unit: unit, bodyfat_pct: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/body-metrics/${athleteId}`);
      setItems(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [athleteId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const body = {
        athlete_id: athleteId,
        date: form.date,
        weight: toCanonicalLb(form.weight, form.weight_unit),
        weight_unit: form.weight_unit || "lb",
        bodyfat_pct: num(form.bodyfat_pct),
        note: form.note || null,
      };
      if (body.weight == null && body.bodyfat_pct == null) {
        toast.error("Enter weight or body-fat %");
        setBusy(false);
        return;
      }
      await api.post("/body-metrics", body);
      toast.success("Logged");
      setForm((f) => ({ ...f, weight: "", bodyfat_pct: "", note: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/body-metrics/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Each entry stores its own unit; convert each one to the user's display unit.
  const chartData = useMemo(() => items.map((m) => ({
    date: m.date,
    // weight is canonical lb; convert to user's display unit
    weight: fromCanonicalLb(m.weight, unit),
    bodyfat_pct: m.bodyfat_pct,
  })), [items, unit]);

  const latest = items[items.length - 1];
  const earliest = items[0];
  const latestWeightDisplay = fromCanonicalLb(latest?.weight, unit);
  const earliestWeightDisplay = fromCanonicalLb(earliest?.weight, unit);
  const deltaW = latestWeightDisplay != null && earliestWeightDisplay != null
    ? +(latestWeightDisplay - earliestWeightDisplay).toFixed(1) : null;

  return (
    <div className="space-y-6">
      {canEdit && (
        <div className="hs-card p-6">
          <div className="flex items-center gap-2">
            <Heartbeat size={18} className="text-terracotta" weight="fill" />
            <h3 className="font-heading text-lg font-semibold">Log body metric</h3>
          </div>
          <form onSubmit={submit} className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="body-metric-form">
            <div className="col-span-2 sm:col-span-1">
              <Label className="hs-label">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="body-date" />
            </div>
            <WeightInput
              className="col-span-1"
              value={form.weight}
              unit={form.weight_unit}
              onChange={(v, u) => setForm({ ...form, weight: v, weight_unit: u })}
              testId="body-weight"
            />
            <div>
              <Label className="hs-label">Body-fat %</Label>
              <Input type="number" step="0.1" min="0" max="80" value={form.bodyfat_pct} onChange={(e) => setForm({ ...form, bodyfat_pct: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="body-fat" />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <Label className="hs-label">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2 h-11 rounded-xl" placeholder="post-travel, morning fasted…" />
            </div>
            <div className="col-span-2 sm:col-span-5">
              <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="submit-body-metric">
                <Plus size={14} className="mr-2" /> {busy ? "Saving…" : "Save entry"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <div className="hs-card p-10 text-center">
          <Heartbeat size={32} className="mx-auto text-ink-3" />
          <p className="mt-3 text-ink-2">No body metrics logged yet.{canEdit ? " Track weight and body-fat % to see the curve." : ""}</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="hs-card p-5" data-testid="body-stat-latest">
              <div className="hs-label text-[10px]">Latest weight ({unit})</div>
              <div className="mt-1 font-data font-semibold text-2xl">{latestWeightDisplay ?? "—"}</div>
              <div className="text-[11px] text-ink-3 mt-0.5">{latest?.date}</div>
            </div>
            <div className="hs-card p-5">
              <div className="hs-label text-[10px]">Body-fat %</div>
              <div className="mt-1 font-data font-semibold text-2xl">{latest?.bodyfat_pct ?? "—"}</div>
              <div className="text-[11px] text-ink-3 mt-0.5">{latest?.bodyfat_pct != null ? latest.date : "not tracked yet"}</div>
            </div>
            <div className="hs-card p-5">
              <div className="hs-label text-[10px]">Change since start ({unit})</div>
              <div className={`mt-1 font-data font-semibold text-2xl ${deltaW != null && deltaW < 0 ? "text-moss" : deltaW != null && deltaW > 0 ? "text-terracotta" : ""}`}>
                {deltaW != null ? `${deltaW > 0 ? "+" : ""}${deltaW}` : "—"}
              </div>
              <div className="text-[11px] text-ink-3 mt-0.5">{earliest?.date && latest?.date ? `${earliest.date} → ${latest.date}` : ""}</div>
            </div>
          </div>

          <div className="hs-card p-6">
            <div className="hs-label">Trend</div>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 6" stroke="#E5E4DE" />
                  <XAxis dataKey="date" stroke="#767976" fontSize={12} />
                  <YAxis yAxisId="left" stroke="#C65D47" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#2D4A3E" fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E4DE" }} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="weight" name={`Weight (${unit})`} stroke="#C65D47" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                  <Line yAxisId="right" type="monotone" dataKey="bodyfat_pct" name="Body-fat %" stroke="#2D4A3E" strokeWidth={3} dot={{ r: 4 }} strokeDasharray="4 4" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="hs-card p-5">
            <div className="hs-label mb-2">Entries</div>
            <div className="divide-y divide-line">
              {[...items].reverse().map((m) => (
                <div key={m.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`body-entry-${m.id}`}>
                  <div className="w-24 font-data text-ink-3">{m.date}</div>
                  <div className="flex-1 flex gap-3 font-data">
                    {m.weight != null && <span><span className="text-ink-3">wt</span> <strong>{formatStoredWeight(m.weight, m.weight_unit || unit)}</strong></span>}
                    {m.bodyfat_pct != null && <span><span className="text-ink-3">bf</span> <strong>{m.bodyfat_pct}%</strong></span>}
                    {m.note && <span className="text-ink-3 italic truncate">"{m.note}"</span>}
                  </div>
                  {canEdit && (
                    <Button size="icon" variant="ghost" onClick={() => del(m.id)} className="rounded-lg text-error hover:bg-red-50 hover:text-error h-8 w-8" data-testid={`delete-body-${m.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export default function Records({ athleteIdOverride }) {
  const params = useParams();
  const { user } = useAuth();
  const unit = getUnit(user);
  const athleteId = athleteIdOverride || params.athleteId || user.id;
  const [prs, setPRs] = useState([]);
  const [condPRs, setCondPRs] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [metric, setMetric] = useState("best_1rm");
  const [athlete, setAthlete] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: prList }, { data: condList }, { data: u }] = await Promise.all([
          api.get(`/stats/prs/${athleteId}`),
          api.get(`/stats/block-prs/${athleteId}`),
          api.get(`/users/${athleteId}`),
        ]);
        setPRs(prList);
        setCondPRs(condList);
        setAthlete(u);
        if (prList[0]) setSelected(prList[0].movement);
      } catch (e) {
        /* noop */
        console.error(formatApiError(e));
      }
    })();
  }, [athleteId]);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      const { data } = await api.get(`/stats/movement-history/${athleteId}`, { params: { movement: selected } });
      setHistory(data);
    })();
  }, [selected, athleteId]);

  const metricLabel = useMemo(() => ({
    max_weight: `Max weight (${unit})`,
    max_reps: "Max reps",
    best_1rm: `Est. 1RM (${unit})`,
    volume: `Volume (${unit})`,
    calories: "Calories",
  }[metric]), [metric, unit]);

  // Convert weight-based metrics for chart display
  const displayHistory = useMemo(() => history.map((h) => ({
    ...h,
    max_weight: fromCanonicalLb(h.max_weight, unit) ?? 0,
    best_1rm: fromCanonicalLb(h.best_1rm, unit) ?? 0,
    volume: fromCanonicalLb(h.volume, unit) ?? 0,
  })), [history, unit]);

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8">
      <BackButton />
      <div>
        <div className="hs-label">Personal records</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 flex items-center gap-3">
          <Trophy size={28} className="text-terracotta" weight="fill" />
          {user.role !== "athlete" ? (athlete?.name || "Athlete") : "Your records"}
        </h1>
        <p className="text-ink-2 mt-1 text-sm">Auto-tracked from every set you log. Epley formula used for 1RM estimates.</p>
      </div>

      <Tabs defaultValue="strength" className="w-full">
        <TabsList className="bg-muted2 rounded-xl p-1">
          <TabsTrigger value="strength" className="rounded-lg data-[state=active]:bg-surface" data-testid="prs-tab-strength">
            <Barbell size={14} className="mr-1.5" /> Strength &amp; movement
          </TabsTrigger>
          <TabsTrigger value="conditioning" className="rounded-lg data-[state=active]:bg-surface" data-testid="prs-tab-conditioning">
            <Timer size={14} className="mr-1.5" /> Conditioning
          </TabsTrigger>
          <TabsTrigger value="body" className="rounded-lg data-[state=active]:bg-surface" data-testid="prs-tab-body">
            <Heartbeat size={14} className="mr-1.5" /> Body
          </TabsTrigger>
        </TabsList>

        <TabsContent value="strength" className="mt-6 space-y-8">
          {prs.length > 0 && (
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
              <Input
                placeholder="Search movements & workouts…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-xl"
                data-testid="records-search"
              />
            </div>
          )}
          {selected && history.length > 0 && (
            <div className="hs-card p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="hs-label">Progress</div>
                  <h2 className="font-heading text-2xl font-semibold">{selected}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={selected} onValueChange={setSelected}>
                    <SelectTrigger className="w-56 rounded-xl h-11" data-testid="chart-movement-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {prs.map((p) => <SelectItem key={p.movement} value={p.movement}>{p.movement}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={metric} onValueChange={setMetric}>
                    <SelectTrigger className="w-44 rounded-xl h-11" data-testid="chart-metric-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="best_1rm">Est. 1RM</SelectItem>
                      <SelectItem value="max_weight">Max weight</SelectItem>
                      <SelectItem value="max_reps">Max reps</SelectItem>
                      <SelectItem value="volume">Volume</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={displayHistory} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 6" stroke="#E5E4DE" />
                    <XAxis dataKey="date" stroke="#767976" fontSize={12} />
                    <YAxis stroke="#767976" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E4DE" }} />
                    <Legend />
                    <Line type="monotone" dataKey={metric} name={metricLabel} stroke="#C65D47" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {prs.length === 0 ? (
            <div className="hs-card p-10 text-center">
              <Trophy size={32} className="mx-auto text-ink-3" />
              <p className="mt-3 text-ink-2">No records yet — start logging sets to unlock your first PR.</p>
              <Link to="/week"><Button className="mt-4 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white">Go to my week</Button></Link>
            </div>
          ) : (
            (() => {
              const q = search.trim().toLowerCase();
              const visible = q
                ? prs.filter((p) => p.movement.toLowerCase().includes(q))
                : prs;
              if (visible.length === 0) {
                return (
                  <div className="hs-card p-10 text-center text-ink-3" data-testid="records-search-empty">
                    No movements match &ldquo;{search}&rdquo;.
                  </div>
                );
              }
              return (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {visible.map((pr) => (
                    <PRCard key={pr.movement} pr={pr} onPick={setSelected} unit={unit} />
                  ))}
                </div>
              );
            })()
          )}
        </TabsContent>

        <TabsContent value="conditioning" className="mt-6">
          {condPRs.length === 0 ? (
            <div className="hs-card p-10 text-center">
              <Timer size={32} className="mx-auto text-ink-3" />
              <p className="mt-3 text-ink-2">
                No conditioning records yet. Complete a MetCon, AMRAP, EMOM or Tabata section to unlock your first benchmark PR.
              </p>
            </div>
          ) : (
            (() => {
              const q = search.trim().toLowerCase();
              const filteredCond = q
                ? condPRs.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.block_type || "").toLowerCase().includes(q))
                : condPRs;
              if (filteredCond.length === 0) {
                return (
                  <div className="hs-card p-10 text-center text-ink-3" data-testid="cond-search-empty">
                    No conditioning records match &ldquo;{search}&rdquo;.
                  </div>
                );
              }
              const benchmarks = filteredCond.filter((p) => BENCHMARK_NAMES.has((p.title || "").trim().toLowerCase()));
              const others = filteredCond.filter((p) => !BENCHMARK_NAMES.has((p.title || "").trim().toLowerCase()));
              return (
                <div className="space-y-8">
                  {benchmarks.length > 0 && (
                    <section>
                      <div className="flex items-baseline gap-2 mb-4">
                        <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                          <Trophy size={18} className="text-terracotta" weight="fill" />
                          Benchmark WODs
                        </h2>
                        <span className="hs-label text-[10px]">{benchmarks.length} tracked</span>
                      </div>
                      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                        {benchmarks.map((pr) => (
                          <ConditioningPRCard key={pr.title} pr={pr} />
                        ))}
                      </div>
                    </section>
                  )}
                  {others.length > 0 && (
                    <section>
                      <div className="flex items-baseline gap-2 mb-4">
                        <h2 className="font-heading text-xl font-semibold">Custom workouts</h2>
                        <span className="hs-label text-[10px]">{others.length} tracked</span>
                      </div>
                      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                        {others.map((pr) => (
                          <ConditioningPRCard key={pr.title} pr={pr} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              );
            })()
          )}
        </TabsContent>

        <TabsContent value="body" className="mt-6">
          <BodyPanel athleteId={athleteId} canEdit={user.role === "athlete" ? athleteId === user.id : true} unit={unit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
